import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService from '../services/authService';
import Spinner from './Spinner';
import { AuthContext } from '../store/ContextStore';
import { AdminTeamOption, CreateUserResponse, TeamSeatInfo } from '../types/adminCreateUser';

interface AdminCreateUserFormProps {
  onCreateSuccess: () => void;
}

interface FormErrors {
  username?: string;
  email?: string;
  fullName?: string;
  password?: string;
  confirmPassword?: string;
  teamId?: string;
  designation?: string;
  general?: string;
}

interface SuccessResult {
  data: CreateUserResponse;
  teamName: string;
}

const ONBOARD_ERROR = 'This team cannot be auto-onboarded. Onboard it manually first or contact support.';
const FEW_HITS_THRESHOLD = 8;
const PAGE_SIZE = 20;
const SCROLL_LOAD_THRESHOLD_PX = 48;

function filterAdminTeamsByQuery(teams: AdminTeamOption[], query: string): AdminTeamOption[] {
  const raw = query.trim();
  const q = raw.toLowerCase();
  if (!q) return [];

  const codeStr = (team: AdminTeamOption) =>
    team.team_code == null ? '' : String(team.team_code);

  const isExactCode = (team: AdminTeamOption) => codeStr(team) === raw;
  const isCodePrefix = (team: AdminTeamOption) => {
    const code = codeStr(team);
    return code.length > 0 && code.startsWith(raw);
  };

  const startsWithMatches: AdminTeamOption[] = [];
  for (const team of teams) {
    if ((team.team_name || '').toLowerCase().startsWith(q)) {
      startsWithMatches.push(team);
    }
  }

  const nameMatches =
    startsWithMatches.length >= FEW_HITS_THRESHOLD
      ? startsWithMatches
      : teams.filter((team) => (team.team_name || '').toLowerCase().includes(q));

  const seen = new Set<number>();
  const result: AdminTeamOption[] = [];

  const pushUnique = (team: AdminTeamOption) => {
    if (seen.has(team.id)) return;
    seen.add(team.id);
    result.push(team);
  };

  for (const team of teams) {
    if (isExactCode(team)) pushUnique(team);
  }
  for (const team of teams) {
    if (isCodePrefix(team)) pushUnique(team);
  }
  for (const team of nameMatches) {
    pushUnique(team);
  }

  return result;
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  if (!q || !text) return <>{text}</>;

  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return <>{text}</>;

  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-blue-100 text-blue-900 font-semibold p-0">
        {text.slice(idx, idx + q.length)}
      </mark>
      {text.slice(idx + q.length)}
    </>
  );
}

const FIELD_MAP: Record<string, keyof FormErrors> = {
  UserName: 'username',
  Email: 'email',
  FullName: 'fullName',
  fullName: 'fullName',
  Password: 'password',
  TeamId: 'teamId',
  Designation: 'designation',
};

const formatTeamLabel = (team: AdminTeamOption) =>
  team.team_code != null ? `${team.team_name} (${team.team_code})` : team.team_name;

const TeamSeatPanel: React.FC<{
  loading: boolean;
  error: string | null;
  seatInfo: TeamSeatInfo | null;
}> = ({ loading, error, seatInfo }) => {
  if (loading) {
    return (
      <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
        <Spinner className="h-4 w-4" />
        Loading seat info...
      </div>
    );
  }

  if (error) {
    return <p className="mt-2 text-sm text-red-600">{error}</p>;
  }

  if (!seatInfo) return null;

  return (
    <div className="mt-2 space-y-1 text-sm">
      <p className="text-gray-700">
        {seatInfo.seats_occupied} / {seatInfo.seat_limit} seats occupied · {seatInfo.seats_remaining} remaining
      </p>
      <p className="text-gray-600">New user will be #{seatInfo.next_user_number} on this team</p>
      {seatInfo.will_be_seat_locked && (
        <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2 text-xs">
          Team is at capacity — new user will be seat-locked until admin approves access.
        </p>
      )}
    </div>
  );
};

const getErrorDetailString = (detail: unknown): string => {
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((entry) => (typeof entry?.msg === 'string' ? entry.msg : ''))
      .filter(Boolean)
      .join(' ');
  }
  return '';
};

const mapCreateUserErrors = (status: number, data: Record<string, unknown> | undefined): FormErrors => {
  if (status === 403) {
    return { general: 'Access denied — admin privileges required' };
  }

  if (status === 404) {
    return { teamId: 'Invalid team selected', general: 'Invalid team selected' };
  }

  const detailStr = getErrorDetailString(data?.detail) || (typeof data?.message === 'string' ? data.message : '');
  const lower = detailStr.toLowerCase();

  if (status === 422) {
    if (lower.includes('onboard') || lower.includes('tidy') || lower.includes('metadata')) {
      return { general: ONBOARD_ERROR };
    }

    if (Array.isArray(data?.detail)) {
      const formatted: FormErrors = {};
      data.detail.forEach((err: { loc?: (string | number)[]; msg?: string }) => {
        const fieldName = String(err.loc?.[1] ?? err.loc?.[0] ?? '');
        const mappedField = FIELD_MAP[fieldName] ?? (fieldName as keyof FormErrors);
        formatted[mappedField] = typeof err.msg === 'string' ? err.msg : String(err.msg ?? 'Invalid value');
      });
      if (Object.keys(formatted).length > 0) return formatted;
    }

    if (typeof data?.detail === 'string') {
      return { general: data.detail };
    }
  }

  if (status === 400 || status === 422) {
    if (lower.includes('email')) return { email: 'Email already registered' };
    if (lower.includes('username') || lower.includes('user name')) return { username: 'Username taken' };
  }

  return { general: detailStr || 'Failed to create user' };
};

const AdminCreateUserForm: React.FC<AdminCreateUserFormProps> = ({ onCreateSuccess }) => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const openToast = authContext?.openToast as ((message: string, color: string) => void) | undefined;

  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [teamId, setTeamId] = useState<number | ''>('');
  const [designation, setDesignation] = useState('');
  const [teams, setTeams] = useState<AdminTeamOption[]>([]);
  const [teamNameQuery, setTeamNameQuery] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<AdminTeamOption | null>(null);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTeamDropdownOpen, setIsTeamDropdownOpen] = useState(false);
  const teamListRef = useRef<HTMLDivElement>(null);
  const teamSearchContainerRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [successResult, setSuccessResult] = useState<SuccessResult | null>(null);

  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [seatInfo, setSeatInfo] = useState<TeamSeatInfo | null>(null);
  const [seatInfoLoading, setSeatInfoLoading] = useState(false);
  const [seatInfoError, setSeatInfoError] = useState<string | null>(null);

  const authService = new AuthService();

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setIsLoadingTeams(true);
        const response = await authService.getAllTeams();
        if (response.status === 200) {
          const teamsData = response.data || [];
          setTeams(Array.isArray(teamsData) ? teamsData : []);
        } else {
          setErrors({ general: 'Failed to load teams' });
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        setErrors({ general: 'An error occurred while fetching teams' });
      } finally {
        setIsLoadingTeams(false);
      }
    };

    fetchTeams();
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
    setActiveIndex(0);
  }, [teamNameQuery]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        teamSearchContainerRef.current &&
        !teamSearchContainerRef.current.contains(event.target as Node)
      ) {
        setIsTeamDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const active = teamListRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    if (!teamId) {
      setSeatInfo(null);
      setSeatInfoError(null);
      setSeatInfoLoading(false);
      return;
    }

    const controller = new AbortController();
    setSeatInfo(null);
    setSeatInfoError(null);
    setSeatInfoLoading(true);

    const fetchSeatInfo = async () => {
      try {
        const response = await authService.getTeamSeatInfo(Number(teamId), controller.signal);

        if (controller.signal.aborted) return;

        if (response.status === 200) {
          setSeatInfo(response.data as TeamSeatInfo);
        } else if (response.status === 404) {
          setSeatInfoError('Team not found');
        } else {
          setSeatInfoError('Failed to load seat info');
        }
      } catch (err: unknown) {
        if (controller.signal.aborted) return;
        const error = err as { code?: string; name?: string };
        if (error.code === 'ERR_CANCELED' || error.name === 'AbortError' || error.name === 'CanceledError') {
          return;
        }
        setSeatInfoError('Failed to load seat info');
      } finally {
        if (!controller.signal.aborted) {
          setSeatInfoLoading(false);
        }
      }
    };

    fetchSeatInfo();

    return () => controller.abort();
  }, [teamId]);

  const clearFieldError = (fieldName: keyof FormErrors) => {
    if (errors[fieldName]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setFullName('');
    setPassword('');
    setConfirmPassword('');
    setTeamId('');
    setDesignation('');
    setSelectedTeam(null);
    setTeamNameQuery('');
    setTestMode(false);
    setSeatInfo(null);
    setSeatInfoError(null);
    setSeatInfoLoading(false);
    setErrors({});
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    if (!username.trim()) {
      newErrors.username = 'Username is required';
      isValid = false;
    }

    if (!email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!fullName.trim()) {
      newErrors.fullName = 'Full name is required';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
      isValid = false;
    }

    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!teamId) {
      newErrors.teamId = 'Team is required';
      isValid = false;
    }

    if (!designation.trim()) {
      newErrors.designation = 'Designation is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const filteredTeams = useMemo(
    () => filterAdminTeamsByQuery(teams, teamNameQuery),
    [teams, teamNameQuery]
  );
  const visibleTeams = filteredTeams.slice(0, visibleCount);
  const hasMoreTeams = filteredTeams.length > visibleCount;

  const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    clearFieldError('username');
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    clearFieldError('email');
  };

  const handleFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
    clearFieldError('fullName');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    clearFieldError('password');
    if (confirmPassword && e.target.value === confirmPassword) {
      clearFieldError('confirmPassword');
    }
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConfirmPassword(e.target.value);
    clearFieldError('confirmPassword');
  };

  const handleDesignationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDesignation(e.target.value);
    clearFieldError('designation');
  };

  const handleTeamSelection = (team: AdminTeamOption) => {
    setSelectedTeam(team);
    setTeamId(team.id);
    setTeamNameQuery('');
    setIsTeamDropdownOpen(false);
    clearFieldError('teamId');
  };

  const loadMoreTeams = () => {
    if (!hasMoreTeams) return;
    setVisibleCount((count) => count + PAGE_SIZE);
  };

  const handleTeamListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - SCROLL_LOAD_THRESHOLD_PX) {
      loadMoreTeams();
    }
  };

  const handleTeamSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!teamNameQuery.trim() || isLoadingTeams) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (!isTeamDropdownOpen) {
        e.preventDefault();
        setIsTeamDropdownOpen(true);
        return;
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (visibleTeams.length === 0) return;
      if (activeIndex >= visibleTeams.length - 1 && hasMoreTeams) {
        loadMoreTeams();
        setActiveIndex((i) => i + 1);
        return;
      }
      setActiveIndex((i) => Math.min(i + 1, visibleTeams.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (visibleTeams.length === 0) return;
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const team = visibleTeams[activeIndex];
      if (team) {
        e.preventDefault();
        handleTeamSelection(team);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsTeamDropdownOpen(false);
    }
  };

  const handleClearTeamSelection = () => {
    setSelectedTeam(null);
    setTeamId('');
    setTeamNameQuery('');
    clearFieldError('teamId');
  };

  const handleViewUsers = () => {
    setSuccessResult(null);
    onCreateSuccess();
  };

  const handleCreateAnother = () => {
    setSuccessResult(null);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setErrors({});

    const payload = {
      UserName: username,
      Email: email,
      fullName: fullName,
      password: password,
      designation: designation,
      TeamId: teamId,
      test: testMode,
    };

    const response = await authService.adminCreateUser(payload);
    const status = response?.status;

    if (status === 201 || status === 200) {
      const data = response.data as CreateUserResponse;
      const teamName = selectedTeam?.team_name || '';

      openToast?.(`User created successfully (ID: ${data.user_id})`, 'green');
      if (data.test_mode) {
        openToast?.('User created in test mode — emails sent to internal recipients only', 'green');
      }
      resetForm();
      setSuccessResult({ data, teamName });
    } else if (status === 401) {
      navigate('/login');
    } else {
      setErrors(mapCreateUserErrors(status ?? 0, response?.data));
    }

    setIsSubmitting(false);
  };

  const sportAccessEntries = successResult?.data.subscription?.sport_access
    ? Object.entries(successResult.data.subscription.sport_access)
    : [];

  if (successResult) {
    const { data, teamName } = successResult;
    const adminUsername = teamName ? `${teamName.replace(/\s+/g, '')}Admin` : 'TeamAdmin';

    return (
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="text-lg font-semibold text-green-800 mb-1">User created successfully</h3>
          <p className="text-sm text-green-700">User ID: {data.user_id}</p>
          {data.message && <p className="text-sm text-green-700 mt-1">{data.message}</p>}
        </div>

        {data.test_mode && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            User created in test mode — emails sent to internal recipients only
          </div>
        )}

        <ul className="space-y-2 mb-4 text-sm text-gray-700">
          {data.team_onboarded && (
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">•</span>
              <span>Team was auto-onboarded</span>
            </li>
          )}
          {data.team_admin_created && (
            <li className="flex items-start gap-2">
              <span className="text-green-600 font-bold">•</span>
              <span>Team admin account was created ({adminUsername} / Athlyte@123)</span>
            </li>
          )}
        </ul>

        {sportAccessEntries.length > 0 && (
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Sport access</h4>
            <div className="overflow-x-auto border border-gray-200 rounded-md">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Sport</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sportAccessEntries.map(([sport, access]) => (
                    <tr key={sport}>
                      <td className="px-3 py-2">{sport}</td>
                      <td className="px-3 py-2 capitalize">{access.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={handleViewUsers}
            className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700"
          >
            View users
          </button>
          <button
            type="button"
            onClick={handleCreateAnother}
            className="bg-white text-gray-700 py-2 px-6 rounded-md border border-gray-300 hover:bg-gray-50"
          >
            Create another
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-6">
        <p className="text-gray-600">Add a new user to the system</p>
      </div>

      {isLoadingTeams && (
        <div className="mb-4 p-3 bg-blue-100 border border-blue-400 text-blue-700 rounded flex items-center">
          <Spinner className="h-4 w-4 mr-2" />
          Loading teams...
        </div>
      )}

      {errors.general && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">Username</label>
            <input
              id="username"
              type="text"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.username ? 'border-red-500' : 'border-gray-300'}`}
              value={username}
              onChange={handleUsernameChange}
              disabled={isSubmitting}
            />
            {errors.username && <p className="mt-1 text-sm text-red-500">{errors.username}</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              id="email"
              type="email"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
              value={email}
              onChange={handleEmailChange}
              disabled={isSubmitting}
            />
            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
          </div>
        </div>

        <div>
          <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
          <input
            id="fullName"
            type="text"
            className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.fullName ? 'border-red-500' : 'border-gray-300'}`}
            value={fullName}
            onChange={handleFullNameChange}
            disabled={isSubmitting}
          />
          {errors.fullName && <p className="mt-1 text-sm text-red-500">{errors.fullName}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                value={password}
                onChange={handlePasswordChange}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {errors.password && <p className="mt-1 text-sm text-red-500">{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
            <div className="relative">
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                value={confirmPassword}
                onChange={handleConfirmPasswordChange}
                disabled={isSubmitting}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {showConfirmPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </>
                  )}
                </svg>
              </button>
            </div>
            {errors.confirmPassword && <p className="mt-1 text-sm text-red-500">{errors.confirmPassword}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label htmlFor="teamSelect" className="block text-sm font-medium text-gray-700 mb-1">
              Select Team *
            </label>
            <div className="relative" ref={teamSearchContainerRef}>
              <div className="flex">
                <input
                  type="text"
                  id="teamSearch"
                  className="w-full p-2 border rounded-l-md border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={isLoadingTeams ? 'Loading teams…' : 'Search for a team'}
                  value={teamNameQuery}
                  onChange={(e) => {
                    setTeamNameQuery(e.target.value);
                    setIsTeamDropdownOpen(true);
                  }}
                  onFocus={() => {
                    if (teamNameQuery.trim()) setIsTeamDropdownOpen(true);
                  }}
                  onKeyDown={handleTeamSearchKeyDown}
                  autoComplete="off"
                  disabled={isSubmitting || isLoadingTeams}
                />
                <button
                  type="button"
                  onClick={() => {
                    if (teamNameQuery) {
                      setTeamNameQuery('');
                      setIsTeamDropdownOpen(false);
                    }
                  }}
                  className="bg-gray-100 text-gray-700 px-3 border border-l-0 border-gray-300 rounded-r-md hover:bg-gray-200"
                  disabled={isSubmitting || isLoadingTeams}
                >
                  {teamNameQuery ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  )}
                </button>
              </div>
              {isTeamDropdownOpen && teamNameQuery.trim().length > 0 && !isLoadingTeams && (
                <div
                  ref={teamListRef}
                  className="mt-2 w-full max-h-[20rem] overflow-auto bg-white border border-gray-300 rounded-md z-10 absolute shadow-lg"
                  onScroll={handleTeamListScroll}
                >
                  {filteredTeams.length === 0 ? (
                    <div className="p-2 text-sm text-gray-500">No teams found</div>
                  ) : (
                    <ul className="divide-y divide-gray-200">
                      {visibleTeams.map((team, index) => {
                        const isActive = index === activeIndex;
                        const code = team.team_code == null ? '' : String(team.team_code);
                        const codePrefixMatch =
                          code.length > 0 && code.startsWith(teamNameQuery.trim());
                        return (
                          <li
                            key={team.id}
                            data-active={isActive ? 'true' : undefined}
                            className={`p-2 cursor-pointer hover:bg-blue-50 ${
                              isActive || selectedTeam?.id === team.id ? 'bg-blue-100' : ''
                            }`}
                            onMouseEnter={() => setActiveIndex(index)}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleTeamSelection(team)}
                          >
                            <div className="flex items-center">
                              {selectedTeam?.id === team.id && (
                                <svg className="w-4 h-4 mr-2 text-blue-600" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              )}
                              <span>
                                <HighlightMatch text={team.team_name} query={teamNameQuery} />
                                {team.team_code != null && (
                                  <>
                                    {' '}
                                    (
                                    {codePrefixMatch ? (
                                      <HighlightMatch text={code} query={teamNameQuery} />
                                    ) : (
                                      team.team_code
                                    )}
                                    )
                                  </>
                                )}
                              </span>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            {selectedTeam && (
              <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-md">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="font-medium text-gray-700">Selected Team:</p>
                    <p className="text-blue-600">{formatTeamLabel(selectedTeam)}</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearTeamSelection}
                    disabled={isSubmitting}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-200 shrink-0"
                    aria-label="Remove selected team"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
                <TeamSeatPanel loading={seatInfoLoading} error={seatInfoError} seatInfo={seatInfo} />
              </div>
            )}
            {errors.teamId && <p className="mt-1 text-sm text-red-500">{errors.teamId}</p>}
          </div>

          <div>
            <label htmlFor="designation" className="block text-sm font-medium text-gray-700 mb-1">Designation</label>
            <input
              id="designation"
              type="text"
              className={`w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.designation ? 'border-red-500' : 'border-gray-300'}`}
              value={designation}
              onChange={handleDesignationChange}
              disabled={isSubmitting}
              placeholder="e.g., Coach, Analyst, Admin"
            />
            {errors.designation && <p className="mt-1 text-xs text-red-500">{errors.designation}</p>}
          </div>
        </div>

        <div className="border-t border-gray-100 pt-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={testMode}
              onChange={(e) => setTestMode(e.target.checked)}
              disabled={isSubmitting}
              className="mt-1 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-700">Test mode</span>
              <span className="block text-xs text-gray-500 mt-0.5">
                Emails go to internal team only, not Kim/Admin or the user
              </span>
            </span>
          </label>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="submit"
            className="bg-blue-600 text-white py-2 px-6 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 flex items-center"
            disabled={isSubmitting || isLoadingTeams}
          >
            {isSubmitting ? (
              <>
                <Spinner className="h-5 w-5 mr-2" />
                <span>Creating...</span>
              </>
            ) : (
              'Create User'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminCreateUserForm;
